/**
 * Trams: Västtrafik's in Gothenburg, on their real lines and timetable
 * (`core/transit`), along the tracks in the streets.
 *
 * A tram runs along its journey's way, the coordinates Västtrafik gives it,
 * which follow the tracks but cut their curves. Its front lays its rail ahead
 * of it, on the tracks under its way (`lay`), and it is drawn on that: three
 * sections of about ten metres, as the low-floor trams are, each between two
 * points on the rail its front has laid, so it bends through a curve as the
 * real one does and its sections go where its front went. It drives itself, not as a car in the
 * traffic: it cannot steer, only go and stop. It keeps its distance from
 * the tram, the car or the player ahead on its way by the Intelligent Driver
 * Model, slows for curves, calls at its stops and paces itself to its
 * timetable as a bus does (`core/transit/service.ts`). The traffic gives way
 * to it: each section is a `Mover` the cars follow, stop for and do not cut
 * across (`Traffic.setMovers`).
 *
 * The look is Gothenburg's: Västtrafik's blue below the windows, a black
 * window band, white above, a pantograph on the middle section's roof, the
 * doors on both sides, and the line's sign at the front and by the front
 * door (`busSigns.ts`). At night the window band is lit from within.
 *
 * Its timetable is Västtrafik's estimated times at each stop, polled every
 * half minute: there are no positions. Between polls it keeps to them,
 * driving to be at its next stop at its time and leaving each at its
 * departure as last known (`core/transit/service.ts`).
 */

import { BoxGeometry, Color, Group, Mesh } from 'three';
import type { MeshStandardMaterial } from 'three';
import type { TransitTrip, TripLayout } from '../core/transit/trip.js';
import { alongNear, pointAlong, scheduledAt } from '../core/transit/trip.js';
import type { BusService } from '../core/transit/service.js';
import { serve, startService, toStop } from '../core/transit/service.js';
import type { Driver } from '../core/traffic/lanes.js';
import { idmAcceleration } from '../core/traffic/lanes.js';
import type { Mover } from './traffic.js';
import { faceted, glass, headlamp, taillamp } from './Vehicle.js';
import type { SignPose } from './busSigns.js';
import type { TrackNetwork, TrackPoint } from './tracks.js';

/** How far from its way a track is still the one it runs on, metres. */
const TRACK_REACH = 7;
/** How far from where a section's end is followed to on its own track the track may be, metres. */
const FOLLOW_REACH = 1.2;
const onTrack: TrackPoint = { x: 0, y: 0, z: 0, dx: 0, dz: 0 };

/** A section's length, the gap between sections, and the width, metres. */
const SECTION = 9.8;
const JOINT = 0.35;
const SECTIONS = 3;
const WIDTH = 2.65;
const LENGTH = SECTIONS * SECTION + (SECTIONS - 1) * JOINT;
const HALF = LENGTH / 2;
/** Top speed in the streets, m/s (50 km/h), and the sideways pull it takes a curve at, m/s². */
const TOP = 50 / 3.6;
const CURVE_PULL = 1.0;
/** A tram: slow away, braking early, keeping well back. */
const TRAM_DRIVER: Driver = { maxAccel: 1.0, comfortBrake: 1.2, minGap: 4, headway: 2 };
/** Most trams about, how far out they are put on and taken off, metres. */
const MAX_TRAMS = 12;
const SPAWN_MIN = 150;
const SPAWN_MAX = 450;
const DESPAWN = 520;
/** How close to its way something must be to be in its way, metres. */
const IN_WAY = 1.9;
/**
 * Going its way, more or less: the cosine between its heading and the way's.
 * A tram on the other track or a car in the other lane coming toward it is
 * beside its way, not in it, and stopping for one stopped both for good.
 */
const SAME_WAY = 0.3;
/** Most a tram waits at a stop for its time, and at its first stop, seconds. */
const TRAM_MOST_DWELL = 40;
const TRAM_FIRST_DWELL = 90;
/** How far to the right of its way a tram looks for its track: Sweden drives on the right. */
const KEEP_RIGHT = 1.4;
/** How far apart the points of its rail are laid along its way, how far ahead of its front, and kept behind its back, metres. */
const RAIL_STEP = 2;
const RAIL_AHEAD = 30;
const RAIL_BEHIND = 10;
/**
 * How far from its way, and how far off its heading (the cosine), a track
 * being followed may run. Generous: its way cuts a curve by up to 12 m and
 * crosses it at a slant. A branch running away at a switch goes further.
 */
const FOLLOW_OFF = 14;
const FOLLOW_TURN = 0.5;
/**
 * At a switch: the track followed and the one under its way are this far
 * apart, and the one followed this much further from its way, metres. Then
 * it is a branch its way leaves by, and the rail takes the other.
 */
const SWITCH_APART = 2.5;
const BRANCH_AWAY = 3;
/** How fast its front on its rail is brought back to where its way has it, m/s at most, moving. */
const CATCH_UP = 1.5;
/** The window band's glow at night, warm white, at full dark. */
const CABIN_LIGHT = new Color(0xffe3b0);
const CABIN_GLOW = 0.9;

/** A tram's rail: points on the tracks under its way, how far along it each is, and the way each faces, forward. */
interface Rail { x: number[]; y: number[]; z: number[]; s: number[]; dx: number[]; dz: number[] }

interface Tram {
  trip: TransitTrip;
  layout: TripLayout;
  service: BusService;
  /** How far along its way its middle is, metres, and its speed, m/s. */
  along: number;
  speed: number;
  group: Group;
  sections: Group[];
  /** Each section's middle and heading, for the cars and the sign. */
  boxes: Array<{ x: number; y: number; z: number; dx: number; dz: number; grade: number }>;
  /** The rail its front has laid; how far along its way it is laid to; and its front's place on it. */
  rail: Rail;
  railWay: number;
  railFront: number;
  id: number;
}

let materials: Record<'blue' | 'white' | 'band' | 'windows' | 'dark' | 'bellows', MeshStandardMaterial> | undefined;
function colours() {
  materials ??= {
    blue: faceted(0x1a9bd7, 0.4, 0.15),
    white: faceted(0xf2f3f1, 0.45, 0.1),
    band: faceted(0x15181c, 0.25, 0.4),
    // The window band behind the glass: dark by day, the lit inside at night.
    windows: faceted(0x15181c, 0.25, 0.4, { emissive: CABIN_LIGHT, emissiveIntensity: 0 }),
    dark: faceted(0x2a2d33, 0.7),
    bellows: faceted(0x1c1e22, 0.9),
  };
  return materials;
}

const emptyRail = (): Rail => ({ x: [], y: [], z: [], s: [], dx: [], dz: [] });

const box = (w: number, h: number, d: number, m: MeshStandardMaterial, x: number, y: number, z: number) => {
  const mesh = new Mesh(new BoxGeometry(w, h, d), m);
  mesh.position.set(x, y, z);
  mesh.castShadow = m !== glass;
  return mesh;
};

/** One section, its middle at the origin on the rails, forward -z. `place` 0 is the front, the last the back. */
function buildSection(place: number): Group {
  const { blue, white, band, windows, dark, bellows } = colours();
  const g = new Group();
  const half = WIDTH / 2, floor = 0.32, belt = 1.05, eave = 2.7, roof = 3.3;
  g.add(box(WIDTH, belt - floor, SECTION, blue, 0, (belt + floor) / 2, 0));
  g.add(box(WIDTH - 0.04, eave - belt, SECTION - 0.04, windows, 0, (eave + belt) / 2, 0));
  g.add(box(WIDTH, roof - eave, SECTION, white, 0, (roof + eave) / 2, 0));
  // A skirt over the bogie, dark, and the bogie's wheels showing under it.
  g.add(box(WIDTH - 0.3, 0.3, 2.4, dark, 0, 0.17, 0));
  // Windows down both sides, and the doors on both.
  for (const side of [-1, 1] as const) {
    for (let z = -SECTION / 2 + 1.0; z < SECTION / 2 - 0.6; z += 1.45) {
      if (Math.abs(z + 2.2) < 0.9 || Math.abs(z - 2.2) < 0.9) continue;
      g.add(box(0.02, eave - belt - 0.25, 1.25, glass, side * (half + 0.004), (eave + belt) / 2 + 0.05, z));
    }
    for (const z of [-2.2, 2.2]) {
      g.add(box(0.03, eave - floor - 0.1, 1.35, blue, side * (half + 0.012), (eave + floor) / 2, z));
      // The inside behind the doors' glass, dark by day, lit at night as the windows are.
      g.add(box(0.01, eave - floor - 0.45, 1.2, windows, side * (half + 0.03), (eave + floor) / 2 + 0.1, z));
      for (const dz of [-0.33, 0.33]) g.add(box(0.03, eave - floor - 0.4, 0.58, glass, side * (half + 0.04), (eave + floor) / 2 + 0.1, z + dz));
    }
  }
  if (place === 0) {
    // The cab: the deep sloped windscreen, the sign band over it, the lamps.
    const front = -SECTION / 2;
    const screen = box(WIDTH - 0.2, eave - belt + 0.2, 0.05, glass, 0, (eave + belt) / 2 + 0.05, front + 0.12);
    screen.rotation.x = -0.12;
    g.add(screen);
    g.add(box(WIDTH - 0.1, belt - floor, 0.3, blue, 0, (belt + floor) / 2, front - 0.1));
    g.add(box(WIDTH - 0.3, 0.32, 0.05, band, 0, eave + 0.3, front - 0.02));
    for (const side of [-1, 1]) g.add(box(0.3, 0.16, 0.06, headlamp, side * 0.95, 0.75, front - 0.26));
  }
  if (place === SECTIONS - 1) {
    const back = SECTION / 2;
    g.add(box(WIDTH - 0.6, 0.9, 0.03, glass, 0, 1.95, back + 0.01));
    for (const side of [-1, 1]) g.add(box(0.22, 0.2, 0.05, taillamp, side * (half - 0.25), 0.8, back + 0.02));
  }
  if (place > 0) g.add(box(WIDTH - 0.4, roof - floor - 0.3, JOINT + 0.1, bellows, 0, (roof + floor) / 2, -SECTION / 2 - JOINT / 2));
  // On the roof: the pantograph on the middle, equipment boxes on the rest.
  if (place === Math.floor(SECTIONS / 2)) {
    g.add(box(1.4, 0.18, 1.4, dark, 0, roof + 0.09, 0));
    const arm = box(0.06, 0.06, 1.7, dark, 0, roof + 0.55, 0.2);
    arm.rotation.x = 0.55;
    g.add(arm);
    const upper = box(0.06, 0.06, 1.6, dark, 0, roof + 1.05, -0.2);
    upper.rotation.x = -0.6;
    g.add(upper);
    g.add(box(1.6, 0.05, 0.12, dark, 0, roof + 1.45, -0.55));
  } else {
    g.add(box(1.5, 0.3, 2.6, white, 0, roof + 0.15, 0));
  }
  return g;
}

export class Trams {
  readonly group = new Group();
  private readonly trams = new Map<string, Tram>();
  private trips: Array<{ trip: TransitTrip; layout: TripLayout }> = [];
  private out = false;
  private serial = 1_000_000;

  /** The trams' trips about, laid out in the scene; as `Traffic.setTransit`. */
  setTrips(trips: Array<{ trip: TransitTrip; layout: TripLayout }>): void {
    this.trips = trips;
    const byId = new Map(trips.map(t => [t.trip.id, t]));
    for (const tram of this.trams.values()) {
      const fresh = byId.get(tram.trip.id);
      if (fresh === undefined) continue;
      tram.trip = fresh.trip;
      // Laid out again where the origin moved: its rail moves with it.
      // Its way changed: laid afresh.
      const old = tram.layout, now = fresh.layout;
      if (old !== now) {
        if (old.xs.length === now.xs.length && old.xs.length > 0) {
          const mx = now.xs[0] - old.xs[0], mz = now.zs[0] - old.zs[0];
          for (let i = 0; i < tram.rail.x.length; i++) { tram.rail.x[i] += mx; tram.rail.z[i] += mz; }
        } else {
          tram.rail = emptyRail();
        }
      }
      tram.layout = fresh.layout;
      tram.service.trip = fresh.trip;
      tram.service.layout = fresh.layout;
    }
  }

  /**
   * Each frame. `ground` is the surface under (x, z) near height y; `cars`
   * the traffic about (`Traffic.around`); (px, pz, py) the player; `night`
   * 0 by day to 1 in the dark, for the lights inside.
   */
  update(dt: number, now: number, px: number, pz: number, py: number,
    ground: (x: number, z: number, y: number) => number | undefined, tracks: TrackNetwork,
    cars: ReadonlyArray<{ x: number; y: number; z: number; dx: number; dz: number; speed: number; halfLength: number }>, night = 0): void {
    dt = Math.min(dt, 0.1);
    colours().windows.emissiveIntensity = night * CABIN_GLOW;
    this.spawn(now, px, pz);
    for (const [id, tram] of this.trams) {
      if (!this.drive(tram, dt, now, px, pz, py, cars)) {
        tram.group.removeFromParent();
        this.trams.delete(id);
        continue;
      }
      this.place(tram, ground, tracks, dt);
      if (Math.hypot(tram.boxes[0].x - px, tram.boxes[0].z - pz) > DESPAWN) {
        tram.group.removeFromParent();
        this.trams.delete(id);
      }
    }
  }

  /** Each section as something the cars must keep clear of. */
  movers(): Mover[] {
    const out: Mover[] = [];
    for (const tram of this.trams.values()) {
      for (const b of tram.boxes) out.push({ x: b.x, y: b.y, z: b.z, dx: b.dx, dz: b.dz, halfLength: SECTION / 2, halfWidth: WIDTH / 2, speed: tram.speed });
    }
    return out;
  }

  /** Where each tram's sign goes: its front section. */
  signs(): SignPose[] {
    const out: SignPose[] = [];
    for (const tram of this.trams.values()) {
      const b = tram.boxes[0];
      out.push({
        id: tram.id, x: b.x, y: b.y, z: b.z, yaw: Math.atan2(-b.dx, -b.dz), grade: b.grade, trip: tram.trip,
        front: -SECTION / 2 - 0.08, frontY: 3.0, side: { x: WIDTH / 2 + 0.03, z: -SECTION / 2 + 1.0, y: 2.45 },
      });
    }
    return out;
  }

  private spawn(now: number, px: number, pz: number): void {
    const min = this.out ? SPAWN_MIN : 40;
    for (const { trip, layout } of this.trips) {
      if (this.trams.size >= MAX_TRAMS) break;
      if (this.trams.has(trip.id)) continue;
      const when = scheduledAt(trip, layout, now);
      if (when.after || (when.before && trip.stops[0].depart - now > 60_000)) continue;
      const p = pointAlong(layout, when.along);
      const d = Math.hypot(p.x - px, p.z - pz);
      if (d < min || d > SPAWN_MAX) continue;
      // Not on top of another on the same tracks: one put on this frame has
      // no sections laid yet, so each is where its way has its middle.
      if ([...this.trams.values()].some(t => {
        const m = pointAlong(t.layout, t.along);
        return Math.hypot(m.x - p.x, m.z - p.z) < LENGTH + 10;
      })) continue;
      const group = new Group();
      const sections = Array.from({ length: SECTIONS }, (_, i) => buildSection(i));
      group.add(...sections);
      this.group.add(group);
      const along = Math.max(HALF, when.along - HALF);
      this.trams.set(trip.id, {
        trip, layout, service: startService(trip, layout, along, TRAM_MOST_DWELL, TRAM_FIRST_DWELL), along, speed: when.at >= 0 ? 0 : TOP * 0.6,
        group, sections, boxes: [], rail: emptyRail(), railWay: 0, railFront: 0, id: this.serial++,
      });
      this.out = true;
    }
  }

  /** One step along its way. False once it is out of service, past its last stop. */
  private drive(tram: Tram, dt: number, now: number, px: number, pz: number, py: number,
    cars: ReadonlyArray<{ x: number; y: number; z: number; dx: number; dz: number; speed: number; halfLength: number }>): boolean {
    const L = tram.layout;
    const at = pointAlong(L, tram.along);
    // Slow for the curves ahead: how sharply the way turns over each stretch of it.
    let desired = TOP;
    for (let d = 5; d < 70; d += 5) {
      const a = pointAlong(L, tram.along + HALF + d - 5), b = pointAlong(L, tram.along + HALF + d);
      const turn = Math.acos(Math.max(-1, Math.min(1, a.dx * b.dx + a.dz * b.dz)));
      if (turn < 0.01) continue;
      const radius = 5 / turn;
      const v = Math.sqrt(CURVE_PULL * radius);
      // Down to it by the time it gets there, braking gently.
      desired = Math.min(desired, Math.sqrt(v * v + 2 * TRAM_DRIVER.comfortBrake * Math.max(0, d - 5)));
    }
    const step = serve(tram.service, at.x, at.z, tram.speed, HALF, desired, dt, now);
    if (step.done) return false;
    desired = Math.min(desired, step.pace);
    let gap = toStop(step.stopGap, TRAM_DRIVER.minGap), leaderSpeed = 0;
    // Whatever is on its way ahead going its way, or across it: another
    // tram, a car, the player. Not what comes the other way beside it.
    const ahead = (x: number, z: number, halfLength: number, speed: number, dx: number, dz: number, anyWay = false) => {
      const p = alongNear(L, x, z, tram.along + 40, 80);
      if (p.off > IN_WAY + 0.6) return;
      const g = p.along - tram.along - HALF - halfLength;
      if (g < -halfLength || g > 90 || g >= gap) return;
      const dir = pointAlong(L, p.along);
      const heading = dx * dir.dx + dz * dir.dz;
      if (!anyWay && heading < -SAME_WAY) return;
      gap = Math.max(0.05, g);
      leaderSpeed = Math.max(0, speed * heading);
    };
    // Each section of another, not only its back: one coming onto its way
    // at a junction is in it from its front on.
    for (const other of this.trams.values()) {
      if (other === tram) continue;
      for (const b of other.boxes) ahead(b.x, b.z, SECTION / 2, other.speed, b.dx, b.dz);
    }
    for (const car of cars) if (Math.abs(car.y - (tram.boxes[0]?.y ?? car.y)) < 3) ahead(car.x, car.z, car.halfLength, car.speed, car.dx, car.dz);
    if (!(Math.abs(py - (tram.boxes[0]?.y ?? py)) > 4)) ahead(px, pz, 0.5, 0, at.dx, at.dz, true);
    const accel = idmAcceleration(tram.speed, Math.max(0.1, desired), gap, leaderSpeed, TRAM_DRIVER);
    tram.speed = Math.max(0, Math.min(TOP, tram.speed + Math.max(-6, accel) * dt));
    tram.along = Math.min(L.length - HALF, tram.along + tram.speed * dt);
    return true;
  }

  /**
   * Lay its rail on ahead of its front, a point every couple of metres along
   * its way, and let go of what is behind its back. Each point is on the
   * track the rail is on, followed on from the last; at a switch, on the
   * one its way goes on by; where none is followed, the nearest track under
   * its way, its own of a double track; where there is no track, its way.
   */
  private lay(tram: Tram, tracks: TrackNetwork, ground: (x: number, z: number, y: number) => number | undefined, front: number, guess: number, was: number): void {
    const r = tram.rail;
    const L = tram.layout;
    const fresh = r.x.length === 0;
    if (fresh) tram.railWay = Math.max(0, front - LENGTH - RAIL_BEHIND) - RAIL_STEP;
    while (tram.railWay < Math.min(L.length, front + RAIL_AHEAD)) {
      tram.railWay = Math.min(L.length, tram.railWay + RAIL_STEP);
      const p = pointAlong(L, tram.railWay);
      const near = (off: number, turn: number) =>
        Math.hypot(onTrack.x - p.x, onTrack.z - p.z) < off && onTrack.dx * p.dx + onTrack.dz * p.dz > turn;
      const n = r.x.length;
      let followed: TrackPoint | undefined, under: TrackPoint | undefined;
      if (n > 0 && tracks.snap(r.x[n - 1] + r.dx[n - 1] * RAIL_STEP, r.z[n - 1] + r.dz[n - 1] * RAIL_STEP, r.dx[n - 1], r.dz[n - 1], FOLLOW_REACH, onTrack) &&
        near(FOLLOW_OFF, FOLLOW_TURN)) followed = { ...onTrack };
      if (tracks.snap(p.x - p.dz * KEEP_RIGHT, p.z + p.dx * KEEP_RIGHT, p.dx, p.dz, TRACK_REACH, onTrack) && near(TRACK_REACH, 0.85)) under = { ...onTrack };
      const off = (q: TrackPoint) => Math.hypot(q.x - p.x, q.z - p.z);
      let at = followed;
      if (at === undefined || (under !== undefined && Math.hypot(at.x - under.x, at.z - under.z) > SWITCH_APART && off(at) > off(under) + BRANCH_AWAY)) at = under;
      at ??= { x: p.x, y: ground(p.x, p.z, guess) ?? was, z: p.z, dx: p.dx, dz: p.dz };
      const gap = n > 0 ? Math.hypot(at.x - r.x[n - 1], at.z - r.z[n - 1]) : 0;
      if (n > 0 && gap < 0.3) continue;
      r.x.push(at.x); r.y.push(at.y); r.z.push(at.z); r.dx.push(at.dx); r.dz.push(at.dz);
      r.s.push(n > 0 ? r.s[n - 1] + gap : 0);
      if (tram.railWay >= L.length) break;
    }
    if (fresh && r.x.length > 0) tram.railFront = this.onRail(tram, pointAlong(L, front), 0, Infinity);
    // Behind its back: let go.
    let drop = 0;
    while (drop + 2 < r.s.length && r.s[drop + 1] < tram.railFront - LENGTH - RAIL_BEHIND) drop++;
    if (drop > 0) for (const list of [r.x, r.y, r.z, r.s, r.dx, r.dz]) list.splice(0, drop);
  }

  /** How far along its rail the point nearest (x, z) is, looking within `reach` of `near`. */
  private onRail(tram: Tram, p: { x: number; z: number }, near: number, reach: number): number {
    const r = tram.rail;
    let best = Infinity, at = near;
    for (let i = 0; i + 1 < r.x.length; i++) {
      if (r.s[i + 1] < near - reach || r.s[i] > near + reach) continue;
      const ax = r.x[i], az = r.z[i], ex = r.x[i + 1] - ax, ez = r.z[i + 1] - az;
      const len2 = ex * ex + ez * ez;
      const t = len2 > 0 ? Math.max(0, Math.min(1, ((p.x - ax) * ex + (p.z - az) * ez) / len2)) : 0;
      const d = Math.hypot(ax + ex * t - p.x, az + ez * t - p.z);
      if (d < best) { best = d; at = r.s[i] + t * (r.s[i + 1] - r.s[i]); }
    }
    return at;
  }

  /** The point `a` along its rail. */
  private railAt(tram: Tram, a: number): { x: number; y: number; z: number } {
    const r = tram.rail;
    const n = r.s.length;
    if (a <= r.s[0]) return { x: r.x[0] - r.dx[0] * (r.s[0] - a), y: r.y[0], z: r.z[0] - r.dz[0] * (r.s[0] - a) };
    if (a >= r.s[n - 1]) return { x: r.x[n - 1], y: r.y[n - 1], z: r.z[n - 1] };
    let lo = 0, hi = n - 1;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (r.s[mid] <= a) lo = mid; else hi = mid;
    }
    const t = (a - r.s[lo]) / ((r.s[hi] - r.s[lo]) || 1);
    return { x: r.x[lo] + (r.x[hi] - r.x[lo]) * t, y: r.y[lo] + (r.y[hi] - r.y[lo]) * t, z: r.z[lo] + (r.z[hi] - r.z[lo]) * t };
  }

  /**
   * Each section where it is: between two points on its rail, its pivots —
   * the front, the middle of each joint, the back — at the rails' height,
   * or the ground's. Its front goes along its rail as fast as it drives,
   * eased toward where its way has it: the rail through a curve is longer
   * than its way across it.
   */
  private place(tram: Tram, ground: (x: number, z: number, y: number) => number | undefined, tracks: TrackNetwork, dt: number): void {
    const front = tram.along + HALF;
    const was = tram.sections[0].position.y;
    const guess = Number.isFinite(was) && was !== 0 ? was : 1e4;
    const ahead = tram.rail.x.length > 0;
    this.lay(tram, tracks, ground, front, guess, was);
    const r = tram.rail;
    if (r.x.length === 0) return;
    if (ahead) {
      const want = this.onRail(tram, pointAlong(tram.layout, front), tram.railFront, 25);
      const pull = CATCH_UP * dt * Math.min(1, tram.speed / 2);
      tram.railFront += tram.speed * dt + Math.max(-pull, Math.min(pull, want - tram.railFront - tram.speed * dt));
    }
    tram.railFront = Math.min(tram.railFront, r.s[r.s.length - 1]);
    // Its pivots behind its front, as far as along it.
    const back = Array.from({ length: SECTIONS + 1 }, (_, k) =>
      k === 0 ? 0 : k === SECTIONS ? LENGTH : k * SECTION + (k - 0.5) * JOINT);
    const pts = back.map(b => this.railAt(tram, tram.railFront - b));
    tram.boxes.length = 0;
    tram.sections.forEach((section, i) => {
      const a = pts[i], b = pts[i + 1];
      // The section's middle: between its pivots, set back by half a joint
      // from one that is a joint, so it is its own length about its middle.
      const dx = a.x - b.x, dz = a.z - b.z, len = Math.hypot(dx, dz) || 1;
      const shift = ((i > 0 ? JOINT / 2 : 0) - (i < SECTIONS - 1 ? JOINT / 2 : 0)) / 2;
      const x = (a.x + b.x) / 2 - (dx / len) * shift, z = (a.z + b.z) / 2 - (dz / len) * shift;
      const y = (a.y + b.y) / 2;
      const grade = (a.y - b.y) / len;
      section.position.set(x, y, z);
      section.rotation.set(Math.atan(grade), Math.atan2(-dx, -dz), 0, 'YXZ');
      tram.boxes.push({ x, y, z, dx: dx / len, dz: dz / len, grade });
    });
  }
}
